import React from 'react';
import { Link } from 'react-router-dom';
import { Wrench, MapPin, Phone, Mail, Instagram, Facebook, Youtube } from 'lucide-react';

const CATEGORIES = ['Drills & Drivers', 'Angle Grinders', 'Rotary Hammers', 'Circular Saws', 'Sanders', 'Impact Wrenches'];
const BRANDS = ['Bosch', 'DeWalt', 'Makita', 'Milwaukee', 'Hikoki', 'Stanley'];

export default function Footer() {
  return (
    <footer className="bg-black border-t border-white/10 pt-16 pb-8">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          {/* Brand */}
          <div>
            <Link to="/" className="flex items-center gap-2 mb-4">
              <div className="w-9 h-9 bg-primary flex items-center justify-center">
                <Wrench size={18} className="text-black" strokeWidth={2.5} />
              </div>
              <div>
                <div className="font-heading font-black text-lg uppercase tracking-tighter text-white leading-none">M N M TOOLS</div>
                <div className="text-primary text-xs font-body tracking-widest uppercase leading-none">Tools Hub</div>
              </div>
            </Link>
            <p className="text-sm text-muted-foreground leading-relaxed mb-4">
              UAE's trusted destination for professional power tools. Genuine products, competitive prices, fast delivery.
            </p>
            <div className="flex gap-3">
              <a href="#" className="w-8 h-8 bg-white/5 hover:bg-primary/20 border border-white/10 flex items-center justify-center text-white/60 hover:text-primary transition-all" aria-label="Instagram">
                <Instagram size={14} />
              </a>
              <a href="#" className="w-8 h-8 bg-white/5 hover:bg-primary/20 border border-white/10 flex items-center justify-center text-white/60 hover:text-primary transition-all" aria-label="Facebook">
                <Facebook size={14} />
              </a>
              <a href="#" className="w-8 h-8 bg-white/5 hover:bg-primary/20 border border-white/10 flex items-center justify-center text-white/60 hover:text-primary transition-all" aria-label="Youtube">
                <Youtube size={14} />
              </a>
            </div>
          </div>

          {/* Categories */}
          <div>
            <h4 className="font-heading font-bold uppercase text-base tracking-wider text-white mb-4">Categories</h4>
            <ul className="space-y-2">
              {CATEGORIES.map(cat => (
                <li key={cat}>
                  <Link to={`/products?category=${encodeURIComponent(cat)}`} className="text-sm text-muted-foreground hover:text-primary transition-colors">
                    {cat}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/products" className="text-sm text-primary font-semibold hover:text-white transition-colors">
                  View All &rarr;
                </Link>
              </li>
            </ul>
          </div>

          {/* Brands */}
          <div>
            <h4 className="font-heading font-bold uppercase text-base tracking-wider text-white mb-4">Top Brands</h4>
            <ul className="space-y-2">
              {BRANDS.map(brand => (
                <li key={brand}>
                  <Link to={`/products?brand=${encodeURIComponent(brand)}`} className="text-sm text-muted-foreground hover:text-primary transition-colors">
                    {brand}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-heading font-bold uppercase text-base tracking-wider text-white mb-4">Contact Us</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-2 text-sm text-muted-foreground">
                <MapPin size={14} className="text-primary mt-0.5 flex-shrink-0" />
                Al Nahda 2, Masafi 2, Dubai, UAE
              </li>
              <li className="flex items-center gap-2 text-sm text-muted-foreground">
                <Phone size={14} className="text-primary flex-shrink-0" />
                +971 4 324 7147
              </li>
              <li className="flex items-center gap-2 text-sm text-muted-foreground">
                <Phone size={14} className="text-primary flex-shrink-0" />
                Mobile: +971 50 957 2652
              </li>
              <li className="flex items-center gap-2 text-sm text-muted-foreground">
                <Mail size={14} className="text-primary flex-shrink-0" />
                info@toolshub.world
              </li>
            </ul>
            <div className="mt-4 p-3 bg-primary/10 border border-primary/20">
              <p className="text-xs text-primary font-bold uppercase tracking-wide">Business Hours</p>
              <p className="text-xs text-white/60 mt-1">Sat–Thu: 9:00 AM – 8:00 PM</p>
              <p className="text-xs text-white/60">Fri: Closed</p>
            </div>
          </div>
        </div>

        {/* Trust badges */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10 border-t border-white/10 pt-8">
          {[
            { icon: '✓', text: 'Genuine Products', sub: '100% authentic tools' },
            { icon: '🚚', text: 'UAE Fast Delivery', sub: '1-3 working days' },
            { icon: '🔒', text: 'Secure Checkout', sub: 'SSL encrypted payments' },
            { icon: '🛠', text: 'Warranty Support', sub: 'Official UAE warranty' },
          ].map((badge, i) => (
            <div key={i} className="flex items-center gap-3">
              <span className="text-xl">{badge.icon}</span>
              <div>
                <p className="text-sm font-bold text-white">{badge.text}</p>
                <p className="text-xs text-muted-foreground">{badge.sub}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-muted-foreground">
            © 2025 M N M Tools Hub. All rights reserved. Dubai, UAE.
          </p>
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <a href="#" className="hover:text-primary transition-colors">Privacy Policy</a>
            <a href="#" className="hover:text-primary transition-colors">Terms of Service</a>
            <a href="#" className="hover:text-primary transition-colors">Return Policy</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
